import { Box, IconButton } from "@material-ui/core";
import { NavigateBefore, NavigateNext } from "@material-ui/icons";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ScrollMenu, VisibilityContext } from "react-horizontal-scrolling-menu";
import useDrag from "../../hooks/useDrag";
import useStyles from "./styles";

type ScrollVisibilityApiType = React.ContextType<typeof VisibilityContext>;

const LeftArrow = () => {
	const { isFirstItemVisible, scrollPrev } = useContext(VisibilityContext);
	const { hidden } = useStyles();

	return (
		<IconButton
			className={isFirstItemVisible ? hidden : undefined}
			onClick={() => scrollPrev()}
			style={{ zIndex: 1, padding: 0, boxShadow: "14px 0px 20px 10px rgb(255 255 255)" }}
		>
			<NavigateBefore />
		</IconButton>
	);
};

const RightArrow = () => {
	const { isLastItemVisible, scrollNext } = useContext(VisibilityContext);
	const { hidden } = useStyles();

	return (
		<IconButton
			className={isLastItemVisible ? hidden : undefined}
			onClick={() => scrollNext()}
			style={{ padding: 0, boxShadow: "-14px 0px 20px 10px rgb(255 255 255)" }}
		>
			<NavigateNext />
		</IconButton>
	);
};

const AutoSize: React.FC<{ children: (width: number, height: number) => React.ReactElement }> = ({ children }) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [{ width, height }, setState] = useState<{ width?: number; height?: number }>({});

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const onResize = () => {
			setState({ width: ref.current?.clientWidth, height: ref.current?.clientHeight });
		};
		onResize();
		window.addEventListener("resize", onResize);

		const resizeObserver = new ResizeObserver((entries) => {
			onResize();
		});

		if (ref.current) {
			resizeObserver.observe(ref.current);
		}

		return () => {
			window.removeEventListener("resize", onResize);
			resizeObserver.disconnect();
		};
	}, [ref.current]);

	return (
		<div ref={ref} style={{ flexGrow: 1, flexShrink: 1, position: "relative" }}>
			{!!width && children(width, height || 1)}
		</div>
	);
};

interface IHorizontalScrollContainerProps {
	children: Array<React.ReactNode>;
}

export const HorizontalScrollContainer = (props: IHorizontalScrollContainerProps) => {
	const { scrollContainer } = useStyles();
	const { dragStart, dragStop, dragMove } = useDrag();
	const ref = useRef<HTMLDivElement>(null);

	const handleDrag =
		({ scrollContainer }: ScrollVisibilityApiType) =>
		(evt: React.MouseEvent<Element, MouseEvent>) =>
			dragMove(evt, (posDiff) => {
				if (scrollContainer.current) {
					scrollContainer.current.scrollLeft += posDiff;
				}
			});

	function onWheel(apiObj: ScrollVisibilityApiType, evt: React.WheelEvent): void {
		const touchpadMovementThreshold = 15;
		const isThouchpad = Math.abs(evt.deltaX) !== 0 || Math.abs(evt.deltaY) < touchpadMovementThreshold;

		if (isThouchpad) {
			evt.stopPropagation();
			return;
		}

		const element = ref.current;
		if (!element) {
			return;
		}

		const { left, top, right, bottom } = element.getBoundingClientRect();
		const { clientX, clientY } = evt;

		const cursorInside = clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
		if (!cursorInside) {
			return;
		}

		if (evt.deltaY < 0) {
			apiObj.scrollNext();
		} else if (evt.deltaY > 0) {
			apiObj.scrollPrev();
		}
	}

	return (
		<AutoSize>
			{(width, _height) => (
				<div style={{ width, position: "absolute", left: 0, top: "-0.75rem" }} className={scrollContainer} ref={ref}>
					<ScrollMenu
						LeftArrow={LeftArrow}
						RightArrow={RightArrow}
						onWheel={onWheel}
						onMouseDown={() => dragStart}
						onMouseUp={() => dragStop}
						onMouseMove={handleDrag}
					>
						{props.children.map((child, index) => (
							<Box id={`element-${index}`}>{child}</Box>
						))}
					</ScrollMenu>
				</div>
			)}
		</AutoSize>
	);
};
